/**
 * Role-based access control service
 *
 * Provides functions to check user roles and authorize access to protected resources.
 */

import { UserRole, UserProfile } from '@/shared/types';

/**
 * Check if a user has admin role
 */
export const isAdmin = (user: UserProfile | null): boolean => {
  return user?.role === UserRole.ADMIN;
};

/**
 * Check if a user has a specific role
 */
export const hasRole = (user: UserProfile | null, role: UserRole): boolean => {
  return user?.role === role;
};

/**
 * Check if a user has any of the specified roles
 */
export const hasAnyRole = (user: UserProfile | null, roles: UserRole[]): boolean => {
  return user ? roles.includes(user.role) : false;
};

/**
 * Authorization error class
 */
export class AuthorizationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AuthorizationError';
  }
}

/**
 * Require that a user has admin role, throw error otherwise
 */
export const requireAdmin = (user: UserProfile | null): void => {
  if (!isAdmin(user)) {
    throw new AuthorizationError('Admin access required');
  }
};

/**
 * Require that a user has a specific role, throw error otherwise
 */
export const requireRole = (user: UserProfile | null, role: UserRole): void => {
  if (!hasRole(user, role)) {
    throw new AuthorizationError(`${role} access required`);
  }
};

/**
 * Require that a user has any of the specified roles, throw error otherwise
 */
export const requireAnyRole = (user: UserProfile | null, roles: UserRole[]): void => {
  if (!hasAnyRole(user, roles)) {
    const roleNames = roles.map(r => {
      switch (r) {
        case UserRole.ADMIN:
          return 'admin';
        case UserRole.FREELANCER:
          return 'freelancer';
        case UserRole.CLIENT:
          return 'client';
      }
    });
    throw new AuthorizationError(`Access restricted to: ${roleNames.join(', ')}`);
  }
};

/**
 * Role-based access control types for route protection
 */
export interface RouteAccessConfig {
  /** Roles allowed to access this route (empty array = public) */
  allowedRoles?: UserRole[];
  /** Redirect path if unauthorized */
  redirectTo?: string;
}

/**
 * Check if access is allowed based on role configuration
 */
export const checkAccess = (
  user: UserProfile | null,
  config: RouteAccessConfig
): { allowed: boolean; redirectTo?: string } => {
  const { allowedRoles = [], redirectTo = '/login' } = config;

  // No roles specified = public access
  if (allowedRoles.length === 0) {
    return { allowed: true };
  }

  // User not authenticated
  if (!user) {
    return { allowed: false, redirectTo };
  }

  // Check if user has any of the allowed roles
  if (hasAnyRole(user, allowedRoles)) {
    return { allowed: true };
  }

  // User doesn't have required role - redirect to home or unauthorized page
  return { allowed: false, redirectTo: redirectTo === '/login' ? '/' : redirectTo };
};
